// src/pages/Utilisateurs/components/UserCompetitionDrawer.jsx
import React, { useEffect } from "react";
import {
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaTimes,
  FaTrophy,
} from "react-icons/fa";

import {
  formatCompetitionDate,
  openCompetitionPublicPage,
  safeDisciplines,
} from "../usersAdminUtils";

export default function UserCompetitionDrawer({ competition, onClose }) {
  useEffect(() => {
    if (!competition) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [competition, onClose]);

  if (!competition) return null;

  const disciplines = safeDisciplines(competition.disciplines_competition);

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-950">
        <div className="relative h-56 shrink-0 overflow-hidden bg-slate-900">
          <img
            src={competition.photo_URL || "/img/no-image.jpg"}
            alt={competition.nom_competition || "Compétition"}
            onError={(event) => {
              event.currentTarget.src = "/img/no-image.jpg";
            }}
            className="h-full w-full object-cover opacity-75"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>

          <div className="absolute bottom-4 left-4 right-4">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white backdrop-blur">
              <FaTrophy />
              Compétition #{competition.id}
            </div>

            <h2 className="text-2xl font-bold text-white sm:text-3xl">
              {competition.nom_competition || "Compétition sans nom"}
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailCard
              icon={FaCalendarAlt}
              label="Date"
              value={formatCompetitionDate(competition.date_competition)}
            />

            <DetailCard
              icon={FaMapMarkerAlt}
              label="Lieu"
              value={competition.lieu_competition || "Lieu non renseigné"}
            />

            <DetailCard
              icon={FaTrophy}
              label="Niveau"
              value={competition.level_competition || "—"}
            />
          </div>

          <section className="mt-4 rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
              Disciplines
            </h3>

            <div className="mt-3 flex flex-wrap gap-2">
              {disciplines.length > 0 ? (
                disciplines.map((discipline, index) => (
                  <span
                    key={`${discipline}-${index}`}
                    className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {discipline}
                  </span>
                ))
              ) : (
                <span className="text-sm text-slate-500">
                  Aucune discipline renseignée.
                </span>
              )}
            </div>
          </section>

          <section className="mt-4 rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
              Horaires de pesées
            </h3>

            {/* texte enrichié avec des sauts de ligne et innerHTML */}
            <p
              className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-200"
              dangerouslySetInnerHTML={{
                __html: competition.horaires_pesees || "Aucun horaire renseigné.",
              }}
            />
          </section>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={() => openCompetitionPublicPage(competition.id)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
            >
              <FaExternalLinkAlt />
              Ouvrir la compétition
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function DetailCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
          <Icon />
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </div>

          <div className="mt-1 font-bold text-slate-900 dark:text-white">
            {value || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}